import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import useDashboardWebSocket from '../../hooks/useDashboardWebSocket';
import EmailVerificationBanner from '../common/EmailVerificationBanner';
import StripeConnectBanner from '../common/StripeConnectBanner';
import TwoFactorReminderBanner from '../common/TwoFactorReminderBanner';
import {
  TrendingUp,
  DollarSign,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  MessageSquare,
  Calendar,
  Users,
  Download,
  Filter,
  Info,
  Settings,
  RefreshCw,
  Send,
  AlertTriangle,
  Activity,
  PoundSterling,
  Wifi,
  WifiOff
} from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';

const ClientDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // WebSocket integration for real-time updates
  const {
    dashboardData: wsDashboardData,
    isConnected: wsConnected,
    isSubscribed: wsSubscribed,
    error: wsError
  } = useDashboardWebSocket();
  const [dashboardData, setDashboardData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('monthly');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);

  // Merge WebSocket data with existing dashboard data
  useEffect(() => {
    if (wsDashboardData && dashboardData) {
      setDashboardData(prev => ({
        ...prev,
        ...wsDashboardData,
        lastUpdated: wsDashboardData.lastUpdated || new Date().toISOString()
      }));
    }
  }, [wsDashboardData]);

  // Fetch dashboard data based on selected period
  const fetchDashboardData = async (period = selectedPeriod, customStart = null, customEnd = null) => {
    try {
      setLoading(true);
      setError(null);
      
      let url = `/api/dashboard/client-summary?period=${period}`;
      if (period === 'custom' && customStart && customEnd) {
        url += `&customStart=${customStart}&customEnd=${customEnd}`;
      }
      
      // Fetching dashboard data from API
      
      const response = await api.get(url);
      
      console.log('🔍 ClientDashboard - Full API Response:', JSON.stringify(response.data, null, 2));
      console.log('🔍 ClientDashboard - Response.data.data:', response.data.data);
      console.log('🔍 ClientDashboard - KPIs (response.data.kpis):', response.data.kpis);
      console.log('🔍 ClientDashboard - KPIs (response.data.data.kpis):', response.data.data?.kpis);
      console.log('🔍 ClientDashboard - Next Payment Due (raw):', response.data.data?.kpis?.nextPaymentDue);
      console.log('🔍 ClientDashboard - Next Payment Due (stringified):', JSON.stringify(response.data.data?.kpis?.nextPaymentDue));
      console.log('🔍 ClientDashboard - Next Payment Due Type:', typeof response.data.data?.kpis?.nextPaymentDue);
      console.log('🔍 ClientDashboard - Next Payment Due Date:', response.data.data?.kpis?.nextPaymentDue?.date);
      console.log('🔍 ClientDashboard - Next Payment Due Date Type:', typeof response.data.data?.kpis?.nextPaymentDue?.date);
      console.log('🔍 ClientDashboard - Next Payment Due Amount:', response.data.data?.kpis?.nextPaymentDue?.amount);
      
      // Unwrap the data if needed
      const dashboardResponse = response.data.data || response.data;
      console.log('🔍 ClientDashboard - Dashboard Response (after unwrap):', JSON.stringify(dashboardResponse, null, 2));
      console.log('🔍 ClientDashboard - Dashboard Response KPIs:', dashboardResponse.kpis);
      console.log('🔍 ClientDashboard - Dashboard Response Next Payment Due:', dashboardResponse.kpis?.nextPaymentDue);
      console.log('🔍 ClientDashboard - Cashflow Forecast:', response.data?.trends?.cashflowForecast);
      console.log('🔍 ClientDashboard - Upcoming Payments:', response.data?.upcomingPayments);
      console.log('🔍 ClientDashboard - Recent Activity:', response.data?.activity?.recentActivity);
      console.log('🔍 ClientDashboard - Recent Activity Count:', response.data?.activity?.recentActivity?.length);
      
      if (response.data.success) {
        // Unwrap the data field if it exists
        const dashboardResponse = response.data.data || response.data;
        setDashboardData(dashboardResponse);
      } else {
        setError('Failed to fetch dashboard data');
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      
      // More specific error messages
      if (err.response?.status === 401) {
        setError('Authentication required. Please log in again.');
      } else if (err.response?.status === 403) {
        setError('Access denied. You do not have permission to view this data.');
      } else if (err.response?.status === 404) {
        setError('Dashboard endpoint not found. Please contact support.');
      } else if (err.response?.status >= 500) {
        setError('Server error. Please try again later.');
      } else if (err.message === 'Network Error') {
        setError('Network error. Please check your connection and try again.');
      } else if (err.response?.data?.message) {
        setError(`Error: ${err.response.data.message}`);
      } else {
        setError('Failed to load dashboard data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle period change
  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    if (period === 'custom') {
      setShowCustomDatePicker(true);
    } else {
      setShowCustomDatePicker(false);
      fetchDashboardData(period);
    }
  };

  // Handle custom date range submission
  const handleCustomDateSubmit = () => {
    if (customDateRange.start && customDateRange.end) {
      fetchDashboardData('custom', customDateRange.start, customDateRange.end);
      setShowCustomDatePicker(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    // Dashboard useEffect - User authentication check
    
    if (!user) {
      console.error('❌ No user found in auth context');
      setError('User not authenticated. Please log in.');
      return;
    }
    
    if (!localStorage.getItem('token')) {
      console.error('❌ No token found in localStorage');
      setError('Authentication token missing. Please log in again.');
      return;
    }
    
    fetchDashboardData();
  }, [user]);

  const formatCurrency = (amount, currency = 'GBP') => {
    if (!amount) return '£0.00';
    
    // For very large amounts, use compact notation
    if (amount >= 1000000) {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: currency,
        notation: 'compact',
        compactDisplay: 'short',
        maximumFractionDigits: 1
      }).format(amount);
    }
    
    // For amounts >= 100,000, show without decimals
    if (amount >= 100000) {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: currency,
        maximumFractionDigits: 0
      }).format(amount);
    }
    
    // For smaller amounts, show with 2 decimal places
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'N/A') return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    } catch (error) {
      return 'N/A';
    }
  };

  // Loading state
  if (loading && !dashboardData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !dashboardData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 text-red-600 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => fetchDashboardData()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // If no data yet, show loading
  if (!dashboardData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Preparing dashboard...</p>
        </div>
      </div>
    );
  }

      // ClientDashboard component rendered successfully with real data

  return (
    <div className="space-y-6 bg-white min-h-screen -m-6 p-6" style={{'--card-bg': '#f9fafb'}}>
      {/* Email Verification Banner - Priority 1 */}
      <EmailVerificationBanner user={user} />
      
      {/* Stripe Connect Banner - Priority 2 (only shows after email verified) */}
      {user?.emailVerified && <StripeConnectBanner user={user} />}

      {/* Two-Factor Reminder Banner - Priority 3 */}
      {user?.emailVerified && <TwoFactorReminderBanner user={user} />}
      
      {/* Header - Modernized */}
      <div className="rounded-xl border border-gray-200 shadow-sm p-6" style={{
        backgroundColor: '#f9fafb',
        background: '#f9fafb',
        backgroundImage: 'none',
        backgroundAttachment: 'initial',
        backgroundClip: 'initial',
        backgroundOrigin: 'initial',
        backgroundPosition: 'initial',
        backgroundRepeat: 'initial',
        backgroundSize: 'initial'
      }}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-sm text-gray-600 mt-1">Welcome back, {user?.firstName}! Here's your financial overview.</p>
            
            {/* Status Indicators - Cleaner layout */}
            <div className="flex items-center gap-4 mt-3">
              {/* WebSocket Status */}
              {wsConnected ? (
                <div className="flex items-center gap-1.5 text-green-600">
                  <Wifi className="w-4 h-4" />
                  <span className="text-xs font-medium">Live updates</span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 text-gray-400">
                  <WifiOff className="w-4 h-4" />
                  <span className="text-xs font-medium">Offline</span>
                </div>
              )}
              
              {/* Period Info */}
              {dashboardData && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Viewing:</span>
                  <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 capitalize">
                    {dashboardData.period}
                  </span>
                  {dashboardData.dateRange && (
                    <span className="text-xs text-gray-500">
                      {dashboardData.dateRange.startFormatted} - {dashboardData.dateRange.endFormatted}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* Controls - Cleaner design */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 border border-gray-200 rounded-lg p-1" style={{backgroundColor: '#f9fafb'}}>
              {['weekly', 'monthly', 'custom'].map((filter) => (
                <button
                  key={filter}
                  onClick={() => handlePeriodChange(filter)}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                    filter === selectedPeriod
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {filter.charAt(0).toUpperCase() + filter.slice(1)}
                </button>
              ))}
            </div>
          
          {/* Custom Date Picker */}
          {showCustomDatePicker && (
            <div className="flex items-center space-x-2 bg-white border border-gray-200 rounded-lg p-2">
              <input
                type="date"
                value={customDateRange.start}
                onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="text-sm border border-gray-300 rounded px-2 py-1"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={customDateRange.end}
                onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="text-sm border border-gray-300 rounded px-2 py-1"
              />
              <button
                onClick={handleCustomDateSubmit}
                className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
              >
                Apply
              </button>
              <button
                onClick={() => {
                  setShowCustomDatePicker(false);
                  setSelectedPeriod('monthly');
                  fetchDashboardData('monthly');
                }}
                className="text-gray-500 hover:text-gray-700 text-sm"
              >
                Cancel
              </button>
            </div>
          )}
            
            {/* Refresh Button */}
            <button
              onClick={() => fetchDashboardData()}
              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
              title="Refresh data"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* SECTION 1: "Where am I now?" - Headline KPIs */}
      <section className="space-y-6" data-tour="dashboard-overview">
        <div className="flex items-center space-x-2">
          <h2 className="text-lg font-semibold text-gray-900">Where am I now?</h2>
          <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Total Value Collected */}
          <div 
            className="group p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer" 
            style={{backgroundColor: '#f9fafb'}} 
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'} 
            onMouseLeave={(e) => e.target.style.backgroundColor = '#f9fafb'}
            onClick={() => navigate('/portal/payments')}
          >
            {/* Top: Icon & Label */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center group-hover:bg-emerald-100 transition-colors">
                  <TrendingUp className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Total Value Collected
                  </p>
                  <p className="text-xs text-gray-400">{dashboardData.period}</p>
                </div>
              </div>
            </div>
            
            {/* Bottom: Number */}
            <div className="text-left">
              <p className="text-3xl font-bold text-gray-900 leading-tight break-words">
                {formatCurrency(dashboardData.kpis.totalValueCollected)}
              </p>
            </div>
          </div>

          {/* Next Payment Due */}
          <div 
            className="group p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer" 
            style={{backgroundColor: '#f9fafb'}} 
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'} 
            onMouseLeave={(e) => e.target.style.backgroundColor = '#f9fafb'}
            onClick={() => navigate('/portal/invoices/received')}
          >
            {/* Top: Icon & Label */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                  <Calendar className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Next Payment Due
                  </p>
                  {dashboardData.kpis.nextPaymentDue && (dashboardData.kpis.nextPaymentDue.date || dashboardData.kpis.nextPaymentDue.dueDate) ? (
                    <p className="text-xs text-gray-400">{formatDate(dashboardData.kpis.nextPaymentDue.date || dashboardData.kpis.nextPaymentDue.dueDate)}</p>
                  ) : (
                    <p className="text-xs text-gray-400">All caught up</p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Bottom: Number */}
            <div className="text-left">
              {dashboardData.kpis.nextPaymentDue && (dashboardData.kpis.nextPaymentDue.date || dashboardData.kpis.nextPaymentDue.dueDate) ? (
                <p className="text-3xl font-bold text-gray-900 leading-tight break-words">
                  {formatCurrency(dashboardData.kpis.nextPaymentDue.amount)}
                </p>
              ) : (
                <p className="text-3xl font-bold text-gray-400">—</p>
              )}
            </div>
          </div>

          {/* Rejected Invoices */}
          <div 
            className="group p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer" 
            style={{backgroundColor: '#f9fafb'}} 
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'} 
            onMouseLeave={(e) => e.target.style.backgroundColor = '#f9fafb'}
            onClick={() => navigate('/portal/invoices/received')}
          >
            {/* Top: Icon & Label */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center group-hover:bg-orange-100 transition-colors">
                  <XCircle className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Rejected Invoices
                  </p>
                  <p className="text-xs text-gray-400">Needs attention</p>
                </div>
              </div>
            </div>
            
            {/* Bottom: Number */}
            <div className="text-left">
              <p className="text-3xl font-bold text-gray-900 leading-tight">
                {dashboardData.kpis.rejectedInvoices}
              </p>
            </div>
          </div>

          {/* Approved Invoices */}
          <div 
            className="group p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer" 
            style={{backgroundColor: '#f9fafb'}} 
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'} 
            onMouseLeave={(e) => e.target.style.backgroundColor = '#f9fafb'}
            onClick={() => navigate('/portal/invoices/received')}
          >
            {/* Top: Icon & Label */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center group-hover:bg-purple-100 transition-colors">
                  <CheckCircle className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Approved Invoices
                  </p>
                  <p className="text-xs text-gray-400">Ready to pay</p>
                </div>
              </div>
            </div>
            
            {/* Bottom: Number */}
            <div className="text-left">
              <p className="text-3xl font-bold text-gray-900 leading-tight">
                {dashboardData.kpis.approvedInvoices}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2: "What's coming?" - Charts & Trends */}
      <section className="space-y-6">
        <div className="flex items-center space-x-2">
          <h2 className="text-lg font-semibold text-gray-900">What's coming?</h2>
          <div className="w-2 h-2 bg-green-600 rounded-full"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upcoming Payments */}
          <div className="p-6 rounded-xl border border-gray-200 shadow-sm" style={{backgroundColor: '#f9fafb'}}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Upcoming Payments</h3>
                <p className="text-sm text-gray-500 mt-1">Next scheduled payments from all received invoices</p>
              </div>
              <div className="group relative">
                <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
                <div className="absolute right-0 top-6 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                  All invoices you've received that are approved and scheduled for automatic payment collection, regardless of when they were created.
                </div>
              </div>
            </div>
            <div className="space-y-3">
              {dashboardData?.upcomingPayments?.upcomingPayments?.length > 0 ? (
                dashboardData.upcomingPayments.upcomingPayments.map((payment, index) => (
                  <div key={payment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-900">
                          {payment.invoiceNumber}
                        </span>
                        <span className="text-xs text-gray-500">
                          from {payment.sender?.companyName || payment.sender?.name || `${payment.sender?.firstName || ''} ${payment.sender?.lastName || ''}`.trim() || payment.senderName || 'Unknown'}
                  </span>
                </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Due {formatDate(payment.dueDate)}
                      </div>
                      {payment.poNumber && (
                        <div className="text-xs text-blue-600 font-medium mt-1">
                          PO: {payment.poNumber}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-gray-900">
                        {formatCurrency(payment.amount)}
                      </div>
                      <div className="text-xs text-gray-500 capitalize">
                        {payment.status}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl text-gray-400">📅</span>
                  </div>
                  <p className="text-sm text-gray-500">No upcoming payments scheduled</p>
                </div>
              )}
            </div>
          </div>

          {/* Cashflow Forecast */}
          <div className="p-6 rounded-xl border border-gray-200 shadow-sm" style={{backgroundColor: '#f9fafb'}}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Cashflow Forecast</h3>
                <div className="flex items-center space-x-2 mt-1">
                  <p className="text-sm text-gray-500">Projected incoming payments from approved invoices</p>
                </div>
              </div>
              <div className="group relative">
                <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
                <div className="absolute right-0 top-6 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                  Rolling forecast showing projected payments from approved invoices awaiting payment over the next 1, 7, and 14 days.
                </div>
              </div>
            </div>
            <div className="space-y-4">
              {dashboardData.trends.cashflowForecast?.slice(0, 3).map((forecast, index) => {
                // Ensure we have proper period labels
                const periodLabels = ['Next 1 day', 'Next 7 days', 'Next 14 days'];
                const displayPeriod = forecast.period || periodLabels[index] || `Period ${index + 1}`;
                
                return (
                  <div key={index} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-700">{displayPeriod}</span>
                        {forecast.count > 0 && (
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                            {forecast.count} invoice{forecast.count !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      <span className="text-sm font-semibold text-gray-900">
                        {formatCurrency(forecast.amount)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="h-3 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-500"
                        style={{
                          width: `${Math.max(...dashboardData.trends.cashflowForecast.map(f => f.amount)) > 0 
                            ? Math.min((forecast.amount / Math.max(...dashboardData.trends.cashflowForecast.map(f => f.amount))) * 100, 100)
                            : 0}%`
                        }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

      </section>

      {/* SECTION 3: "What should I act on?" - Activity Feed & Alerts */}
      <section className="space-y-6">
        <div className="flex items-center space-x-2">
          <h2 className="text-lg font-semibold text-gray-900">What should I act on?</h2>
          <div className="w-2 h-2 bg-red-600 rounded-full"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Activity Feed */}
          <div className="rounded-xl border border-gray-200 shadow-sm" style={{backgroundColor: '#f9fafb'}}>
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
              <h3 className="text-base font-medium text-gray-900">Recent Activity</h3>
                  <div className="flex items-center space-x-2">
              <p className="text-xs text-gray-500">Latest invoice updates and approvals</p>
                    {dashboardData && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 capitalize">
                        {dashboardData.period} period
                      </span>
                    )}
                  </div>
                </div>
                <div className="group relative">
                  <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
                  <div className="absolute right-0 top-6 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                    Real-time feed of recent invoice activities including approvals, rejections, and payments.
                  </div>
                </div>
              </div>
            </div>
            <div className="p-6">
              {dashboardData.activity?.recentActivity?.length > 0 ? (
                <div className="space-y-4">
                  {dashboardData.activity.recentActivity.slice(0, 6).map((activity) => {
                    // Define activity type configurations with new color + icon system
                    const activityConfig = {
                      invoice_sent: {
                        icon: Send,
                        bgColor: 'bg-blue-50',
                        iconColor: 'text-blue-600',
                        borderColor: 'border-blue-200',
                        label: 'Sent'
                      },
                      invoice_approved: {
                        icon: CheckCircle,
                        bgColor: 'bg-yellow-50',
                        iconColor: 'text-yellow-600',
                        borderColor: 'border-yellow-200',
                        label: 'Approved'
                      },
                      invoice_rejected: {
                        icon: XCircle,
                        bgColor: 'bg-red-50',
                        iconColor: 'text-red-600',
                        borderColor: 'border-red-200',
                        label: 'Rejected'
                      },
                      payment_received: {
                        icon: '💰', // Money in icon
                        bgColor: 'bg-green-50',
                        iconColor: 'text-green-600',
                        borderColor: 'border-green-200',
                        label: 'Earned'
                      },
                      payment_made: {
                        icon: '💸', // Money out icon
                        bgColor: 'bg-red-50',
                        iconColor: 'text-red-600',
                        borderColor: 'border-red-200',
                        label: 'Paid'
                      },
                      payment_failed: {
                        icon: AlertTriangle,
                        bgColor: 'bg-orange-50',
                        iconColor: 'text-orange-600',
                        borderColor: 'border-orange-200',
                        label: 'Failed'
                      },
                      pending_approval: {
                        icon: Clock,
                        bgColor: 'bg-yellow-50',
                        iconColor: 'text-yellow-600',
                        borderColor: 'border-yellow-200',
                        label: 'Pending'
                      }
                    };

                    const config = activityConfig[activity.type] || activityConfig.invoice_sent;
                    
                    // Handle emoji icons vs React components
                    const isEmojiIcon = typeof config.icon === 'string';
                    const IconComponent = isEmojiIcon ? null : config.icon;

                    return (
                      <div 
                        key={activity.id} 
                        onClick={() => {
                          if (activity.invoiceId) {
                            navigate(`/portal/invoices?highlight=${activity.invoiceId}`);
                          }
                        }}
                        className={`flex items-center space-x-3 p-3 ${config.bgColor} rounded-lg border ${config.borderColor} cursor-pointer hover:shadow-md transition-shadow duration-200 ${activity.invoiceId ? 'hover:scale-[1.02]' : 'cursor-default'}`}
                      >
                        {isEmojiIcon ? (
                          <span className="text-lg">{config.icon}</span>
                        ) : (
                          <IconComponent className={`w-5 h-5 ${config.iconColor}`} />
                        )}
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {activity.description}
                          </p>
                          <div className="flex items-center space-x-2 mt-1">
                            <p className="text-xs text-gray-500">
                              {formatDate(activity.date)}
                            </p>
                            {activity.amount && (
                              <>
                                <span className="text-xs text-gray-400">•</span>
                                <p className="text-xs font-medium text-gray-700">
                                  {formatCurrency(activity.amount, activity.currency)}
                                </p>
                                <span className="text-xs text-gray-400">•</span>
                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                  config.label === 'Earned' ? 'bg-green-100 text-green-700' :
                                  config.label === 'Paid' ? 'bg-red-100 text-red-700' :
                                  config.label === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {config.label}
                                </span>
                              </>
                            )}
                            {activity.invoiceId && (
                              <>
                                <span className="text-xs text-gray-400">•</span>
                                <p className="text-xs text-[#1F11ED] font-medium">
                                  Click to view
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Activity className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p>No recent activity</p>
                  <p className="text-xs text-gray-400 mt-1">Activity will appear here as invoices are processed</p>
                </div>
              )}
            </div>
          </div>

          {/* Revenue Performance */}
          <div className="rounded-xl border border-gray-200 shadow-sm" style={{backgroundColor: '#f9fafb'}}>
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Revenue Performance</h3>
                  <p className="text-sm text-gray-500">Revenue earned over time periods</p>
            </div>
                <div className="group relative">
                  <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
                  <div className="absolute right-0 top-6 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                    Track your revenue performance over the last 30, 60, and 90 days with percentage changes compared to previous periods.
                  </div>
                </div>
              </div>
            </div>
            <div className="p-6">
              {dashboardData.revenue?.revenuePerformance && 
               dashboardData.revenue.revenuePerformance.last30Days && 
               dashboardData.revenue.revenuePerformance.last60Days && 
               dashboardData.revenue.revenuePerformance.last90Days ? (
                <div className="space-y-6">
                  {/* Main Revenue Overview */}
                  <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-6 text-white">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="text-lg font-semibold mb-1">Total Revenue (90 Days)</h4>
                        <p className="text-indigo-100 text-sm">Comprehensive revenue tracking</p>
                      </div>
                      <PoundSterling className="w-8 h-8 text-indigo-200" />
                    </div>
                    <div className="text-4xl font-bold mb-2">
                      {formatCurrency(dashboardData.revenue.revenuePerformance.last90Days.amount)}
                    </div>
                    <div className="flex items-center space-x-4 text-sm">
                      <span className="text-indigo-100">
                        {dashboardData.revenue.revenuePerformance.last90Days.change > 0 ? '+' : ''}
                        {dashboardData.revenue.revenuePerformance.last90Days.change.toFixed(1)}% vs previous 90 days
                  </span>
                      {dashboardData.revenue.revenuePerformance.last90Days.trend === 'up' && (
                        <TrendingUp className="w-4 h-4 text-green-300" />
                      )}
                      {dashboardData.revenue.revenuePerformance.last90Days.trend === 'down' && (
                        <TrendingUp className="w-4 h-4 text-red-300 rotate-180" />
                      )}
                </div>
                    <div className="mt-2 text-xs text-indigo-200">
                      Previous 90 days: {formatCurrency(dashboardData.revenue.revenuePerformance.last90Days.previousAmount || 0)}
                    </div>
                  </div>

                  {/* Period Breakdown */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Last 30 Days */}
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-5 border border-blue-200">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-blue-800">Last 30 Days</h4>
                        {dashboardData.revenue.revenuePerformance.last30Days.trend === 'up' && (
                          <TrendingUp className="w-5 h-5 text-green-600" />
                        )}
                        {dashboardData.revenue.revenuePerformance.last30Days.trend === 'down' && (
                          <TrendingUp className="w-5 h-5 text-red-600 rotate-180" />
                        )}
                        {dashboardData.revenue.revenuePerformance.last30Days.trend === 'neutral' && (
                          <div className="w-5 h-5 bg-gray-400 rounded-full" />
                        )}
                      </div>
                      <div className="text-3xl font-bold text-blue-900 mb-2">
                        {formatCurrency(dashboardData.revenue.revenuePerformance.last30Days.amount)}
                      </div>
                      <div className={`text-sm font-medium ${
                        dashboardData.revenue.revenuePerformance.last30Days.change > 0 ? 'text-green-600' :
                        dashboardData.revenue.revenuePerformance.last30Days.change < 0 ? 'text-red-600' :
                        'text-gray-600'
                      }`}>
                        {dashboardData.revenue.revenuePerformance.last30Days.change > 0 ? '+' : ''}
                        {dashboardData.revenue.revenuePerformance.last30Days.change.toFixed(1)}% vs previous 30 days
                      </div>
                      <div className="mt-3 text-xs text-blue-600">
                        Previous 30 days: {formatCurrency(dashboardData.revenue.revenuePerformance.last30Days.previousAmount || 0)}
                      </div>
                    </div>

                    {/* Last 60 Days */}
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-5 border border-green-200">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-green-800">Last 60 Days</h4>
                        {dashboardData.revenue.revenuePerformance.last60Days.trend === 'up' && (
                          <TrendingUp className="w-5 h-5 text-green-600" />
                        )}
                        {dashboardData.revenue.revenuePerformance.last60Days.trend === 'down' && (
                          <TrendingUp className="w-5 h-5 text-red-600 rotate-180" />
                        )}
                        {dashboardData.revenue.revenuePerformance.last60Days.trend === 'neutral' && (
                          <div className="w-5 h-5 bg-gray-400 rounded-full" />
                        )}
                      </div>
                      <div className="text-3xl font-bold text-green-900 mb-2">
                        {formatCurrency(dashboardData.revenue.revenuePerformance.last60Days.amount)}
                      </div>
                      <div className={`text-sm font-medium ${
                        dashboardData.revenue.revenuePerformance.last60Days.change > 0 ? 'text-green-600' :
                        dashboardData.revenue.revenuePerformance.last60Days.change < 0 ? 'text-red-600' :
                        'text-gray-600'
                      }`}>
                        {dashboardData.revenue.revenuePerformance.last60Days.change > 0 ? '+' : ''}
                        {dashboardData.revenue.revenuePerformance.last60Days.change.toFixed(1)}% vs previous 60 days
                      </div>
                      <div className="mt-3 text-xs text-green-600">
                        Previous 60 days: {formatCurrency(dashboardData.revenue.revenuePerformance.last60Days.previousAmount || 0)}
                      </div>
                    </div>
                  </div>

                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <PoundSterling className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-700 mb-2">Revenue Performance Loading...</h3>
                  <p className="text-sm">Please refresh the page if this persists</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Top Paying Customers */}
        <div className="bg-white rounded-xl border shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Top Paying Customers</h3>
                <div className="flex items-center space-x-2">
                  <p className="text-sm text-gray-500">Your highest revenue contributors</p>
                  {dashboardData && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 capitalize">
                      {dashboardData.period} period
                    </span>
                  )}
                </div>
              </div>
              <div className="group relative">
                <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
                <div className="absolute right-0 top-6 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                  Customers ranked by total invoice value, helping you identify your most valuable business relationships.
                </div>
              </div>
            </div>
          </div>
          <div className="p-6">
            {dashboardData.customers?.topCustomers?.length > 0 ? (
              <div className="space-y-4">
                {dashboardData.customers.topCustomers.slice(0, 3).map((customer, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-blue-600">
                          {index + 1}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{customer.name}</p>
                        <p className="text-sm text-gray-500">
                          {formatCurrency(customer.totalRevenue || customer.amount || 0)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p>No customer data available for this period</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <div className="rounded-xl border border-gray-200 shadow-sm p-6" style={{backgroundColor: '#f9fafb'}} data-tour="dashboard-actions">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Link
            to="/portal/send-invoice"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors duration-200"
          >
            <FileText className="w-6 h-6 text-blue-600 mr-3" />
            <div>
              <p className="font-medium text-gray-900">Send Invoice</p>
              <p className="text-sm text-gray-500">Create new invoice</p>
            </div>
          </Link>

          <Link
            to="/portal/invoices/received"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-green-300 hover:bg-green-50 transition-colors duration-200"
          >
            <Download className="w-6 h-6 text-green-600 mr-3" />
            <div>
              <p className="font-medium text-gray-900">View Received</p>
              <p className="text-sm text-gray-500">Check incoming</p>
            </div>
          </Link>

          <Link
            to="/portal/chat"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors duration-200"
          >
            <MessageSquare className="w-6 h-6 text-purple-600 mr-3" />
            <div>
              <p className="font-medium text-gray-900">Chat Support</p>
              <p className="text-sm text-gray-500">Get help</p>
            </div>
          </Link>

          <Link
            to="/portal/settings"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-colors duration-200"
          >
            <Settings className="w-6 h-6 text-gray-600 mr-3" />
            <div>
              <p className="font-medium text-gray-900">Settings</p>
              <p className="text-sm text-gray-500">Configure account</p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ClientDashboard;
